
import Vue from 'vue';
import Multiselect from 'vue-multiselect';
import { Clause, Label } from '@/model';
import {
  distinct, groupBy, notEmpty,
} from '@/util/array-util';
import labels, { createSearchableString, getUseContext, labelSorter } from '@/store/labels';
import LabelEditor from '@/components/LabelEditor.vue';
import LabelChip from '@/components/LabelChip.vue';
import deepEqual from '@/util/deep-equals';
import api from '@/rest-api';

type LabelGroup = { group: string; members: readonly Label[] };

export default Vue.extend({
  name: 'LabelPicker',
  components: { Multiselect, LabelEditor, LabelChip },

  props: {
    value: { type: Array as () => Label[], required: true },
    placeholder: { type: String, default: 'Labels' },
    context: { type: Array as () => Label[], default: () => [] }, // For example document labels
    single: { type: Boolean, default: false },
    closeOnSelect: { type: Boolean, default: false },
    editExisting: { type: Boolean, default: false },
    showLimit: { type: Number, default: 50 },
    focus: { type: Boolean, default: false },
    documentId: { type: Number as () => number | undefined },
    potential: { type: Boolean, default: false },
    relatedLabels: { type: Array as () => Label[], default: () => [] },
    priorityTypes: { type: Array as () => string[], default: () => [] },
    priorityGroupTitle: { type: String, default: 'Pinned:' },
    hideSettings: { type: Boolean, default: false },
  },

  data() {
    return {
      editItem: undefined as Label | undefined,
      createCounter: 0,
      recentlyUsed: [] as number[],
      searching: false,
      potentialLabels: [] as Label[],
      contextLabels: [] as Label[],
      loading: false,
      contextLoading: false,
      currentTab: undefined as number | undefined,
      settings: {
        search: {
          excludeTypes: ['ThreatCategory'] as string[],
        },
        potential: {
          confidence: 50,
          limit: 20,
        },
        recently: {
          save: 100,
          limit: 20,
          weighted: true,
        },
        context: {
          lables: [] as number[],
          contextTypes: ['Threat'],
          includeTypes: [
            'Customer',
            'Industry',
            'Product',
            'Transaction',
            'Channel',
            'Geography',
          ],
        },
        pinned: [] as number[] | undefined,
      },
      basedContext: [] as number[],
    };
  },

  mounted() {
    const recentlyUsed = localStorage.getItem('labelPicker.recentlyUsed');
    if (recentlyUsed) {
      this.recentlyUsed = JSON.parse(recentlyUsed);
    }
    const settings = localStorage.getItem('labelPicker.settings');
    if (settings) {
      this.settings = { ...this.settings, ...JSON.parse(settings) };
    }
    const tab = localStorage.getItem('labelPicker.tab');
    if (tab) {
      const parsed = JSON.parse(tab);
      const maxTabIndex = this.settingsTabIndex - (this.hideSettings ? 1 : 0);
      // Only restore the sticky tab if it is still valid for the currently enabled tabs.
      if (parsed >= 0 && parsed <= maxTabIndex) {
        this.currentTab = parsed;
      }
    }

    if (this.focus) {
      this.$nextTick(() => {
        (this.$refs.selector as any).activate();
      });
    }
    // register the keydown event
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  watch: {
    recentlyUsed: {
      handler() {
        localStorage.setItem('labelPicker.recentlyUsed', JSON.stringify(this.recentlyUsed));
      },
      deep: true,
    },
    settings: {
      handler() {
        localStorage.setItem('labelPicker.settings', JSON.stringify(this.settings));
        this.basedContext = [];
      },
      deep: true,
    },
    currentTab: {
      // Sticky tab
      handler() {
        localStorage.setItem('labelPicker.tab', JSON.stringify(this.currentTab));
      },
      deep: true,
    },
    value: {
      handler(to, from) {
        if (!deepEqual(to, from)) {
          this.loadPotential();
        }
      },
      immediate: true,
    },
    basedContext: {
      handler(to, from) {
        if (!deepEqual(to, from) && to.length > 0) {
          this.loadContext();
        }
      },
      immediate: true,
    },
  },

  computed: {
    useContext(): boolean {
      return getUseContext();
    },
    // Tab indices depend on which optional tabs (Potential, Context) are enabled,
    // since disabling one shifts the position of the ones that follow it.
    potentialTabIndex(): number | undefined {
      return this.potential ? 1 : undefined;
    },
    contextTabIndex(): number | undefined {
      return this.useContext ? 1 + (this.potential ? 1 : 0) : undefined;
    },
    settingsTabIndex(): number {
      return 1 + (this.potential ? 1 : 0) + (this.useContext ? 1 : 0);
    },
    multiple(): boolean {
      return !this.single;
    },
    edit(): boolean {
      return !!this.editItem;
    },
    searchOptions(): LabelGroup[] {
      return Array.from(groupBy(this.available, (label) => label.type))
        .map(([group, members]) => ({ group, members }))
        .filter((g) => !this.settings.search.excludeTypes.find((t) => t === g.group));
    },
    options(): LabelGroup[] {
      if (this.potential && this.currentTab === this.potentialTabIndex) {
        return [
          {
            group: 'Potential:',
            members: this.potentialLabels,
          },
        ];
      }
      if (this.useContext && this.currentTab === this.contextTabIndex) {
        return Array.from(
          groupBy(
            this.available.filter((l) => this.contextLabels.find((c) => c.id === l.id)),
            (label) => label.type,
          ),
        ).map(([group, members]) => ({ group, members }));
      }
      return [
        ...(this.priorityLabels.length > 0 ? [{ group: this.priorityGroupTitle, members: this.priorityLabels }] : []),
        { group: 'Pinned:', members: this.displayPinnedLabels },
        { group: 'Recently used:', members: this.displayTopRecently },
      ];
    },
    available(): readonly Label[] {
      return labels.state.labels.filter((l) => l.selectable) || [];
    },
    hasConfiguredPriorityTypes(): boolean {
      return this.priorityTypesNormalized.length > 0;
    },
    priorityTypesNormalized(): string[] {
      return distinct(this.priorityTypes.map((type) => type.trim()).filter((type) => type.length > 0));
    },
    selectedLabelIds(): number[] {
      return this.value.map((label) => label.id);
    },
    topRecently(): Label[] {
      const ru = distinct(this.settings.recently.weighted
        ? Array.from(groupBy(this.recentlyUsed, (id) => id))
          .map(([label, members]) => ({ label, count: members.length }))
          .sort((a, b) => b.count - a.count)
          .map((g) => g.label) : this.recentlyUsed);
      return ru.map((l) => this.available.find((a) => a.id === l))
        .filter(notEmpty)
        .slice(0, this.settings.recently.limit);
    },
    priorityLabels(): Label[] {
      if (!this.hasConfiguredPriorityTypes) {
        return [];
      }
      const selectedLabelIds = new Set(this.selectedLabelIds);
      return this.available
        .filter((label) => this.priorityTypesNormalized.includes(label.type) && !selectedLabelIds.has(label.id))
        .sort(labelSorter(['type', 'length', 'alpha']));
    },
    contextOptions(): Label[] {
      const apa = this.context
        .map((c) => c.id)
        .map((l) => labels.getters.lookup.get(l))
        .filter(notEmpty);
      return [...apa, ...this.value]
        .filter((l) => this.settings.context.contextTypes.find((t) => t === l.type));
    },

    labelTypes(): string[] {
      return distinct(this.available.map((l) => l.type)).filter(
        (t) => t.trim().length > 0,
      );
    },
    allLabels(): Label[] {
      return [...labels.state.labels];
    },
    pinnedLabels(): Label[] {
      if (!this.settings.pinned) {
        return [];
      }
      return this.settings.pinned
        .map((l) => this.available.find((a) => a.id === l))
        .filter(notEmpty);
    },
    displayPinnedLabels(): Label[] {
      if (!this.hasConfiguredPriorityTypes) {
        return this.pinnedLabels;
      }
      const excludedIds = new Set([...this.selectedLabelIds, ...this.priorityLabels.map((label) => label.id)]);
      return this.pinnedLabels.filter((label) => !excludedIds.has(label.id));
    },
    displayTopRecently(): Label[] {
      if (!this.hasConfiguredPriorityTypes) {
        return this.topRecently;
      }
      const excludedIds = new Set([
        ...this.selectedLabelIds,
        ...this.priorityLabels.map((label) => label.id),
        ...this.displayPinnedLabels.map((label) => label.id),
      ]);
      return this.topRecently.filter((label) => !excludedIds.has(label.id));
    },
    suggestRelatedLabels(): Label[] {
      if (!this.relatedLabels) {
        return [];
      }
      return this.relatedLabels
        .filter((l) => !this.value.find((a) => a.id === l.id));
    },
  },

  methods: {
    isPinned(label: Label): boolean {
      return !!this.settings.pinned?.includes(label.id);
    },
    togglePin(label: Label) {
      const pinned = this.settings.pinned ?? [];
      this.settings.pinned = this.isPinned(label)
        ? pinned.filter((id) => id !== label.id)
        : [...pinned, label.id];
    },
    async loadPotential() {
      if (!this.potential) {
        return;
      }
      this.loading = true;
      this.potentialLabels = Array.from(
        groupBy(
          (
            await api.potentialLabels({
              explicit: this.value.map((l) => l.id),
              implicit: this.context.map((l) => l.id),
              confidence: this.settings.potential.confidence / 100,
              subsetRules: true, // Check sub rules given a selection
            })
          ).flatMap((i) => i.to.map((id) => ({ id, ...i }))),
          (r) => r.id,
        ),
      )
        .map(([rule, value]) => ({
          rule,
          confidence: Math.max(...value.map((v) => v.confidence)),
        }))
        .sort((a, b) => b.confidence - a.confidence)
        .map((a) => a.rule)
        .slice(0, this.settings.potential.limit)
        .map((l) => this.available.find((a) => a.id === l))
        .filter(notEmpty);
      this.loading = false;
    },
    async loadContext() {
      this.contextLoading = true;

      // const clause: Clause = { type: 'in', field: 'label', values: this.basedContext };
      const clause: Clause = {
        type: 'or',
        clauses: this.basedContext.map((l) => ({
          type: 'in',
          field: 'label',
          values: [l],
        })),
      };
      this.contextLabels = (await api.queryLabels({ clause }))
        .map((l) => labels.getters.lookup.get(l))
        .filter(notEmpty)
        .filter((l) => this.settings.context.includeTypes.find((t) => t === l.type));
      this.contextLoading = false;
    },
    input(vs: Label[]) {
      const nv = vs
        .map((l) => l.id)
        .filter((lid) => lid > 0)
        .filter((lid) => !this.value.find((selected) => lid === selected.id));
      this.recentlyUsed = [...nv, ...this.recentlyUsed].slice(
        0,
        this.settings.recently.save,
      ); // only save the recently 100 used
      this.$emit('input', vs);
    },
    create(code: string) {
      this.editItem = {
        id: 0 - this.createCounter,
        type: '',
        code,
        color: { r: 211, g: 211, b: 211 },
        selectable: true,
      };
      this.createCounter += 1;
    },
    save(label: Label | undefined) {
      if (label) {
        labels.dispatch.save(label).then((saved) => {
          this.editItem = undefined;
          this.input([...this.value, saved]);
        });
      }
    },
    unselect(label: Label, _: Event) {
      const vs = this.value.filter((l) => l.id !== label.id);
      this.input(vs);
      // hack to close the input box. Event.stopPropagation does not work here
      (this.$refs.selector as any).deactivate();
    },
    searchableString(label: Label) {
      return createSearchableString(label);
    },
    searchChange(searchString: string | undefined) {
      this.searching = !!searchString && searchString.trim().length > 0;
    },
    handleKeydown(e: KeyboardEvent) {
      // ctrl + 1, 2, 3, 4 for pinned labels, upto 9
      if (e.ctrlKey && e.key.match(/[1-9]/)) {
        const index = parseInt(e.key, 10) - 1;
        if (index < this.pinnedLabels.length) {
          // remove the pinned label if it is already selected
          const pinned = this.pinnedLabels[index];
          if (this.value.find((l) => l.id === pinned.id)) {
            this.input(this.value.filter((l) => l.id !== pinned.id));
          } else {
            this.input([...this.value, pinned]);
          }
        }
        e.preventDefault();
      }
    },
  },
});
